import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Home, RefreshCw, Smartphone, UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { PushNotificationAction, SendPushNotificationRequest } from '@/domain/entities/push-notification';
import {
  fetchHomeownerDevices,
  fetchHomeownerById,
  fetchHomeownerProperties,
  selectFetchHomeownerByIdError,
  selectHasFetchedHomeownerById,
  selectHasFetchedHomeownerDevicesByUserId,
  selectHasFetchedHomeownerPropertiesByUserId,
  selectHomeownerDevicesByUserId,
  selectHomeownerDevicesErrorByUserId,
  selectHomeownerDevicesLoadingByUserId,
  selectHomeownerById,
  selectHomeownerPropertiesByUserId,
  selectHomeownerPropertiesErrorByUserId,
  selectHomeownerPropertiesLoadingByUserId,
  selectIsSendingPushNotification,
  selectIsFetchingHomeownerById,
  selectSendPushNotificationError,
  sendPushNotification,
} from '@/features/homeowners';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type HomeownerDetailsTab = 'information' | 'properties' | 'devices';

function getDisplayName(firstName?: string, lastName?: string, email?: string) {
  const fullName = `${firstName?.trim() ?? ''} ${lastName?.trim() ?? ''}`.trim();
  return fullName || email || 'Unknown user';
}

function formatDate(value?: number) {
  if (!value) return 'N/A';

  const timestamp = value < 1_000_000_000_000 ? value * 1000 : value;
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatBoolean(value?: boolean) {
  return value ? 'Yes' : 'No';
}

function getDeviceLabel(device: { deviceInfo?: { deviceName?: string }; deviceId: string; platform: string }) {
  return device.deviceInfo?.deviceName?.trim() || `${device.platform} device`;
}

function parseFlatStringRecord(raw: string): Record<string, string> | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Data must be a JSON object.');
  }

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== 'string') {
      throw new Error(`Data value for "${key}" must be a string.`);
    }
    result[key] = value;
  }

  return result;
}

function parseNotificationActions(raw: string): PushNotificationAction[] | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const parsed = JSON.parse(trimmed) as unknown;
  if (!Array.isArray(parsed)) {
    throw new Error('Actions must be a JSON array.');
  }

  return parsed.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`Action ${index + 1} must be an object.`);
    }

    const action = item as Partial<PushNotificationAction>;
    if (typeof action.title !== 'string' || !action.title.trim()) {
      throw new Error(`Action ${index + 1} title is required.`);
    }
    if (typeof action.action !== 'string' || !action.action.trim()) {
      throw new Error(`Action ${index + 1} action is required.`);
    }

    return {
      title: action.title,
      action: action.action,
      ...(typeof action.primary === 'boolean' ? { primary: action.primary } : {}),
    };
  });
}

function parseOptionalNonNegativeInteger(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${label} must be an integer greater than or equal to 0.`);
  }

  return parsed;
}

function getPropertyAddress(property: {
  address?: string | { address?: string | null; city?: string | null; state?: string | null; province?: string | null };
}) {
  if (typeof property.address === 'string') return property.address;

  return [property.address?.address, property.address?.city, property.address?.state ?? property.address?.province]
    .filter(Boolean)
    .join(', ');
}

export default function HomeownerDetailsPage() {
  const { homeownerId } = useParams<{ homeownerId: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const resolvedHomeownerId = homeownerId ? decodeURIComponent(homeownerId) : '';
  const homeowner = useAppSelector(selectHomeownerById(resolvedHomeownerId));
  const isLoading = useAppSelector(selectIsFetchingHomeownerById(resolvedHomeownerId));
  const error = useAppSelector(selectFetchHomeownerByIdError(resolvedHomeownerId));
  const hasFetchedHomeowner = useAppSelector(selectHasFetchedHomeownerById(resolvedHomeownerId));
  const devices = useAppSelector(selectHomeownerDevicesByUserId(resolvedHomeownerId));
  const isLoadingDevices = useAppSelector(selectHomeownerDevicesLoadingByUserId(resolvedHomeownerId));
  const devicesError = useAppSelector(selectHomeownerDevicesErrorByUserId(resolvedHomeownerId));
  const hasFetchedDevices = useAppSelector(selectHasFetchedHomeownerDevicesByUserId(resolvedHomeownerId));
  const isSendingPushNotification = useAppSelector(selectIsSendingPushNotification);
  const sendPushNotificationError = useAppSelector(selectSendPushNotificationError);
  const homeownerProperties = useAppSelector(selectHomeownerPropertiesByUserId(resolvedHomeownerId));
  const isLoadingProperties = useAppSelector(selectHomeownerPropertiesLoadingByUserId(resolvedHomeownerId));
  const hasFetchedProperties = useAppSelector(selectHasFetchedHomeownerPropertiesByUserId(resolvedHomeownerId));
  const propertiesError = useAppSelector(selectHomeownerPropertiesErrorByUserId(resolvedHomeownerId));
  const [activeTab, setActiveTab] = useState<HomeownerDetailsTab>('information');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushData, setPushData] = useState('');
  const [pushSound, setPushSound] = useState('default');
  const [pushBadge, setPushBadge] = useState('');
  const [pushImageUrl, setPushImageUrl] = useState('');
  const [pushActions, setPushActions] = useState('');
  const [pushAutoCloseDelay, setPushAutoCloseDelay] = useState('');
  const [pushForceDataOnly, setPushForceDataOnly] = useState(false);
  const [pushFormError, setPushFormError] = useState<string | null>(null);
  const cameFromHomeownersList =
    Boolean(
      location.state &&
        typeof location.state === 'object' &&
        'fromHomeownersList' in location.state &&
        (location.state as { fromHomeownersList?: boolean }).fromHomeownersList,
    ) && Boolean(homeowner);
  const displayName = useMemo(
    () => getDisplayName(homeowner?.firstName, homeowner?.lastName, homeowner?.email),
    [homeowner?.email, homeowner?.firstName, homeowner?.lastName],
  );

  useEffect(() => {
    if (!resolvedHomeownerId) return;
    if (homeowner) return;
    if (isLoading || hasFetchedHomeowner) return;

    dispatch(fetchHomeownerById({ userId: resolvedHomeownerId }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load home owner');
      });
  }, [dispatch, hasFetchedHomeowner, homeowner, isLoading, resolvedHomeownerId]);

  useEffect(() => {
    if (activeTab !== 'properties') return;
    if (!resolvedHomeownerId) return;
    if (hasFetchedProperties || isLoadingProperties) return;

    dispatch(fetchHomeownerProperties({ userID: resolvedHomeownerId }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load properties');
      });
  }, [activeTab, dispatch, hasFetchedProperties, isLoadingProperties, resolvedHomeownerId]);

  useEffect(() => {
    if (activeTab !== 'devices') return;
    if (!resolvedHomeownerId) return;
    if (hasFetchedDevices || isLoadingDevices) return;

    dispatch(fetchHomeownerDevices({ userID: resolvedHomeownerId }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load devices');
      });
  }, [activeTab, dispatch, hasFetchedDevices, isLoadingDevices, resolvedHomeownerId]);

  const selectedDevice = useMemo(
    () => devices.find((device) => device.id === selectedDeviceId) ?? null,
    [devices, selectedDeviceId],
  );

  useEffect(() => {
    if (devices.length === 0) {
      if (selectedDeviceId) {
        setSelectedDeviceId('');
      }
      return;
    }

    if (selectedDeviceId && devices.some((device) => device.id === selectedDeviceId)) return;
    setSelectedDeviceId(devices[0].id);
  }, [devices, selectedDeviceId]);

  const resetPushDialog = () => {
    setPushTitle('');
    setPushBody('');
    setPushData('');
    setPushSound('default');
    setPushBadge('');
    setPushImageUrl('');
    setPushActions('');
    setPushAutoCloseDelay('');
    setPushForceDataOnly(false);
    setPushFormError(null);
  };

  const handleRefreshInformation = () => {
    if (!resolvedHomeownerId) return;

    void dispatch(fetchHomeownerById({ userId: resolvedHomeownerId, force: true }))
      .unwrap()
      .catch((err) => toast.error(err?.message || 'Failed to refresh home owner information'));
  };

  const handleRefreshProperties = () => {
    if (!resolvedHomeownerId) return;

    void dispatch(fetchHomeownerProperties({ userID: resolvedHomeownerId, force: true }))
      .unwrap()
      .catch((err) => toast.error(err?.message || 'Failed to refresh properties'));
  };

  const handleRefreshDevices = () => {
    if (!resolvedHomeownerId) return;

    void dispatch(fetchHomeownerDevices({ userID: resolvedHomeownerId, force: true }))
      .unwrap()
      .catch((err) => toast.error(err?.message || 'Failed to refresh devices'));
  };

  const handleSendPushNotification = async () => {
    if (!homeowner) return;

    if (!selectedDevice) {
      setPushFormError('Select a device before sending a notification.');
      return;
    }

    const title = pushTitle.trim();
    const body = pushBody.trim();

    if (!title) {
      setPushFormError('Title is required.');
      return;
    }
    if (!body) {
      setPushFormError('Body is required.');
      return;
    }

    try {
      const data = parseFlatStringRecord(pushData);
      const actions = parseNotificationActions(pushActions);
      const badge = parseOptionalNonNegativeInteger(pushBadge, 'Badge');
      const autoCloseDelay = parseOptionalNonNegativeInteger(pushAutoCloseDelay, 'Auto-close delay');
      const imageUrl = pushImageUrl.trim();

      if (imageUrl) {
        new URL(imageUrl);
      }

      const request: SendPushNotificationRequest = {
        userID: homeowner.applicationUserId,
        deviceID: selectedDevice.deviceId,
        title,
        body,
        ...(data ? { data } : {}),
        ...(pushSound.trim() ? { sound: pushSound.trim() } : {}),
        ...(badge !== undefined ? { badge } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(actions ? { actions } : {}),
        ...(autoCloseDelay !== undefined ? { autoCloseDelay } : {}),
        forceDataOnly: pushForceDataOnly,
      };

      setPushFormError(null);
      const response = await dispatch(sendPushNotification(request)).unwrap();
      if (response.data.deliveryStatus === 'partially_succeeded') {
        toast.warning(response.data.message);
      } else {
        toast.success(response.data.message || 'Notification sent');
      }
      setPushDialogOpen(false);
      resetPushDialog();
    } catch (err) {
      const error = err as { message?: string };
      setPushFormError(error?.message || 'Failed to send push notification.');
    }
  };

  if (!homeowner && resolvedHomeownerId && (isLoading || !hasFetchedHomeowner)) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  if (!homeowner) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/admin/homeowners">Back to home owners</Link>
          </Button>
          {hasFetchedHomeowner ? (
            <Button type="button" variant="outline" onClick={handleRefreshInformation} disabled={isLoading}>
              Retry
            </Button>
          ) : null}
        </div>
        <ErrorNotice message={error || 'Home owner not found.'} title="Failed to Load Home Owner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cameFromHomeownersList ? (
        <RouteBreadcrumb items={[{ label: 'Home Owners', to: '/admin/homeowners' }, { label: displayName }]} />
      ) : null}

      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Home Owner · {displayName}</h1>
          <p className="text-muted-foreground text-sm">{homeowner.applicationUserId}</p>
          <ErrorNotice className="mt-2" message={error} title="Failed to Load Home Owner" />
        </div>

        <Button asChild variant="outline">
          <Link to="/admin/homeowners">Back to home owners</Link>
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as HomeownerDetailsTab)}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="information" className="gap-2">
            <UserRound className="h-4 w-4" />
            Information
          </TabsTrigger>
          <TabsTrigger value="properties" className="gap-2">
            <Home className="h-4 w-4" />
            Properties
          </TabsTrigger>
          <TabsTrigger value="devices" className="gap-2">
            <Smartphone className="h-4 w-4" />
            Devices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="information" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div className="space-y-1.5">
                <CardTitle>Information</CardTitle>
                <CardDescription>Home owner profile fields returned by the admin API.</CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleRefreshInformation}
                disabled={isLoading}
                aria-label="Refresh information"
                title="Refresh information"
              >
                <RefreshCw className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              </Button>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={homeowner.firstName ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={homeowner.lastName ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={homeowner.email ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={homeowner.phone ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Email Verified</Label>
                <Input value={formatBoolean(homeowner.emailVerified)} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Phone Verified</Label>
                <Input value={formatBoolean(homeowner.phoneVerified)} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex h-10 items-center">
                  {homeowner.status ? <Badge variant="secondary">{homeowner.status}</Badge> : <span>N/A</span>}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Input value={homeowner.role ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Input value={homeowner.typeName ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Application User ID</Label>
                <Input value={homeowner.applicationUserId} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Created</Label>
                <Input value={formatDate(homeowner.createdAt)} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Updated</Label>
                <Input value={formatDate(homeowner.updatedAt)} readOnly />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="properties" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div className="space-y-1.5">
                <CardTitle>Properties</CardTitle>
                <CardDescription>Properties associated with this home owner.</CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleRefreshProperties}
                disabled={isLoadingProperties}
                aria-label="Refresh properties"
                title="Refresh properties"
              >
                <RefreshCw className={isLoadingProperties ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <ErrorNotice message={propertiesError} title="Failed to Load Properties" />

              {isLoadingProperties && !hasFetchedProperties ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <LoadingSpinner size={48} />
                </div>
              ) : homeownerProperties.length === 0 ? (
                <div className="border-muted-foreground/25 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
                  <Home className="text-muted-foreground mb-2 h-8 w-8" />
                  <h3 className="font-medium">No properties found</h3>
                  <p className="text-muted-foreground text-sm">
                    This home owner is not linked to any loaded properties.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {homeownerProperties.map((property) => (
                      <TableRow key={property.id}>
                        <TableCell>
                          <Button asChild variant="link" className="h-auto p-0">
                            <Link
                              to={`/properties/${encodeURIComponent(property.id)}/edit`}
                              state={{
                                fromHomeownerProperties: true,
                                homeownerId: homeowner.applicationUserId,
                                homeownerLabel:
                                  displayName !== 'Unknown user'
                                    ? displayName
                                    : homeowner.phone || homeowner.applicationUserId,
                              }}
                            >
                              {property.title || property.id}
                            </Link>
                          </Button>
                        </TableCell>
                        <TableCell>{getPropertyAddress(property) || 'N/A'}</TableCell>
                        <TableCell>
                          {property.status ? <Badge variant="secondary">{property.status}</Badge> : 'N/A'}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{property.orgID || 'N/A'}</TableCell>
                        <TableCell>{formatDate(property.updatedAt || property.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Devices</h2>
              <p className="text-muted-foreground text-sm">Send a push notification to the selected device.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleRefreshDevices}
              disabled={isLoadingDevices}
              aria-label="Refresh devices"
              title="Refresh devices"
            >
              <RefreshCw className={isLoadingDevices ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
          </div>

          <ErrorNotice message={devicesError} title="Failed to Load Devices" />
          <ErrorNotice message={sendPushNotificationError} title="Failed to Send Notification" />

          {isLoadingDevices && !hasFetchedDevices ? (
            <div className="flex min-h-[180px] items-center justify-center">
              <LoadingSpinner size={48} />
            </div>
          ) : devices.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="border-muted-foreground/25 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
                  <Smartphone className="text-muted-foreground mb-2 h-8 w-8" />
                  <h3 className="font-medium">No devices found</h3>
                  <p className="text-muted-foreground text-sm">
                    This home owner has no registered push notification devices.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
              <Card>
                <CardHeader>
                  <CardTitle>Devices</CardTitle>
                  <CardDescription>Select a registered push notification device.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {devices.map((device) => {
                    const isActive = device.id === selectedDeviceId;
                    const notificationsEnabled = device.deviceInfo?.notificationSettings?.globalNotificationsEnabled;

                    return (
                      <button
                        key={device.id}
                        type="button"
                        onClick={() => setSelectedDeviceId(device.id)}
                        className={[
                          'w-full rounded-md border p-3 text-left transition',
                          isActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{getDeviceLabel(device)}</p>
                            <p className="text-muted-foreground truncate text-xs">{device.deviceId}</p>
                            <p className="text-muted-foreground mt-1 truncate text-xs">
                              Last active: {formatDate(device.lastActive)}
                            </p>
                          </div>

                          <div className="flex flex-wrap justify-end gap-2">
                            <Badge variant="secondary">{device.platform}</Badge>
                            {notificationsEnabled ? (
                              <Badge variant="default">Enabled</Badge>
                            ) : (
                              <Badge variant="outline">Disabled</Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div className="space-y-1.5">
                    <CardTitle>Device Details</CardTitle>
                    <CardDescription>Selected device metadata and push notification token.</CardDescription>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setPushDialogOpen(true)}
                    disabled={isSendingPushNotification || !selectedDevice}
                    className="shrink-0"
                  >
                    {isSendingPushNotification ? 'Sending...' : 'Send Notification'}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  {selectedDevice ? (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
                          <Smartphone className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{getDeviceLabel(selectedDevice)}</p>
                          <p className="text-muted-foreground truncate text-xs">{selectedDevice.id}</p>
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Platform</Label>
                          <Input value={selectedDevice.platform} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>Installation ID</Label>
                          <Input value={selectedDevice.deviceId} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>Device Name</Label>
                          <Input value={selectedDevice.deviceInfo?.deviceName ?? ''} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>OS Version</Label>
                          <Input value={String(selectedDevice.deviceInfo?.osVersion ?? '')} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>Global Notifications</Label>
                          <Input
                            value={formatBoolean(
                              selectedDevice.deviceInfo?.notificationSettings?.globalNotificationsEnabled,
                            )}
                            readOnly
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Last Active</Label>
                          <Input value={formatDate(selectedDevice.lastActive)} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>Created</Label>
                          <Input value={formatDate(selectedDevice.createdAt)} readOnly />
                        </div>

                        <div className="space-y-2">
                          <Label>Updated</Label>
                          <Input value={formatDate(selectedDevice.updatedAt)} readOnly />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Device Token</Label>
                        <div
                          className="bg-muted/40 min-h-10 rounded-md border px-3 py-2 font-mono text-xs break-all"
                          title={selectedDevice.deviceToken}
                        >
                          {selectedDevice.deviceToken || 'N/A'}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">Select a device to review details.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={pushDialogOpen}
        onOpenChange={(open) => {
          if (isSendingPushNotification) return;
          setPushDialogOpen(open);
          if (!open) resetPushDialog();
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Push Notification</DialogTitle>
            <DialogDescription>
              This sends one notification to {selectedDevice ? getDeviceLabel(selectedDevice) : 'the selected device'}{' '}
              for {displayName}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <ErrorNotice message={pushFormError} title="Notification validation failed" />

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="push-title">Title</Label>
                <Input
                  id="push-title"
                  value={pushTitle}
                  onChange={(event) => setPushTitle(event.target.value)}
                  disabled={isSendingPushNotification}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="push-sound">Sound</Label>
                <Input
                  id="push-sound"
                  value={pushSound}
                  onChange={(event) => setPushSound(event.target.value)}
                  placeholder="default"
                  disabled={isSendingPushNotification}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-body">Body</Label>
              <Textarea
                id="push-body"
                value={pushBody}
                onChange={(event) => setPushBody(event.target.value)}
                disabled={isSendingPushNotification}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="push-badge">Badge</Label>
                <Input
                  id="push-badge"
                  type="number"
                  min="0"
                  value={pushBadge}
                  onChange={(event) => setPushBadge(event.target.value)}
                  disabled={isSendingPushNotification}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="push-auto-close">Auto-close Delay</Label>
                <Input
                  id="push-auto-close"
                  type="number"
                  min="0"
                  value={pushAutoCloseDelay}
                  onChange={(event) => setPushAutoCloseDelay(event.target.value)}
                  placeholder="8000"
                  disabled={isSendingPushNotification}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-image-url">Image URL</Label>
              <Input
                id="push-image-url"
                value={pushImageUrl}
                onChange={(event) => setPushImageUrl(event.target.value)}
                placeholder="https://example.com/image.png"
                disabled={isSendingPushNotification}
              />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Force data-only delivery</p>
                <p className="text-muted-foreground text-xs">Send only the data payload without notification UI.</p>
              </div>
              <Switch
                checked={pushForceDataOnly}
                onCheckedChange={setPushForceDataOnly}
                disabled={isSendingPushNotification}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-data">Data JSON</Label>
              <Textarea
                id="push-data"
                value={pushData}
                onChange={(event) => setPushData(event.target.value)}
                placeholder={'{"type":"support_ticket","ticketID":"ticket-123"}'}
                disabled={isSendingPushNotification}
              />
              <p className="text-muted-foreground text-xs">Optional flat JSON object with string values.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-actions">Actions JSON</Label>
              <Textarea
                id="push-actions"
                value={pushActions}
                onChange={(event) => setPushActions(event.target.value)}
                placeholder={'[{"title":"View","action":"open_ticket","primary":true}]'}
                disabled={isSendingPushNotification}
              />
              <p className="text-muted-foreground text-xs">Optional array of action objects.</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPushDialogOpen(false);
                resetPushDialog();
              }}
              disabled={isSendingPushNotification}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSendPushNotification} disabled={isSendingPushNotification}>
              {isSendingPushNotification ? 'Sending...' : 'Send Notification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
